// The script view, the second of the three views. The episode header (preview, tabs, Add line / Add picture) over a
// read-only list in time order, grouped by picture. Nothing is edited or selected here: tapping a row opens its page.
// "Add line" and "Add picture" add at the end; the "+ Add here" row at the foot of each picture's card adds there.
// Quick and the full timeline are the other views.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { decodeImages, imageSrc } from "../../library/images";
import { imagesInRange } from "../../engine/timelineFrame";
import { useLibrary } from "../../library/store";
import { findAsset } from "../../model/library";
import { lineById, canPlay, displayDuration, markById, pictureAt, scriptRows, type ScriptBadges, type ScriptRow } from "../../model/timeline";
import { Avatar, Icon, Page, secs, useTimelinePlayback } from "../common";
import { EditorBar, type View } from "./EditorShell";
import { EpisodeHead, Tile } from "./EpisodeHead";
import { FullPlayer } from "./FullPlayer";
import { placeholderToFill } from "./pages/shared";
import { fmtT } from "./Stage";
import { useTimeline, type EditorProps } from "./TimelineLoader";
import type { Open } from "./EditorShell";
import { useEditorPages } from "./EditorShell";
import { motionJob, picJob, useJobs } from "../jobs";
import "./script.css";

// Camera cuts and expression changes inside a line: small muted notes.
function Notes({ b }: { b: ScriptBadges }) {
  if (b.cuts <= 0 && b.feelings <= 0) return null;
  return (
    <span className="sv-notes">
      {b.cuts > 0 && <span><Icon name="video" size={14} />{b.cuts === 1 ? "camera cut" : `${b.cuts} camera cuts`}</span>}
      {b.feelings > 0 && <span><Icon name="face" size={14} />{b.feelings === 1 ? "expression change" : `${b.feelings} expression changes`}</span>}
    </span>
  );
}

// What the line a mid-line picture starts in is called: the nearest row above that is one.
function aboveNoun(rows: ScriptRow[], r: ScriptRow): string {
  for (let i = rows.indexOf(r) - 1; i >= 0; i--) { const x = rows[i]!; if (x.kind === "line") return x.how === "silent" ? "silence" : "line"; }
  return "line";
}

export function ScriptView({ onBack, view, onView }: EditorProps & { view: View; onView: (v: View) => void }) {
  const { ep } = useTimeline();
  const lib = useLibrary();
  const rows = useMemo(() => scriptRows(ep), [ep]);
  const total = displayDuration(ep);
  const playable = canPlay(ep);
  const [t, setT] = useState(0);
  const pb = useTimelinePlayback(ep, lib);
  const want = useRef<number | null>(null); // a playhead move asked for by a page, applied once the episode it changed has been drawn
  const pages = useEditorPages(ep, pb.stop, time => { want.current = time; });
  // Pictures and animations being drawn in the background show on their rows; a new picture shows at the foot until it lands.
  const jobs = useJobs();
  const drawingNew = [...jobs.keys()].filter(k => k.startsWith("pic:") && !ep.pictures.some(p => picJob(p.id) === k)).length;
  const list = useRef<HTMLDivElement>(null);

  const shownT = Math.min(pb.t ?? t, total);
  const playing = pb.t !== null ? rows.find(r => r.kind === "line" && pb.t! >= r.t - 1e-6 && pb.t! < r.end - 1e-6) : undefined;

  useEffect(() => { if (pb.t !== null) setT(pb.t); }, [pb.t]);
  useLayoutEffect(() => { if (want.current !== null) { setT(want.current); want.current = null; } }, [ep]);
  useEffect(() => { decodeImages(imagesInRange(ep, 0, Infinity)).catch(() => {}); }, [imagesInRange(ep, 0, Infinity).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the playing row in view.
  const follow = playing?.key;
  useEffect(() => {
    if (!follow) return;
    const el = [...(list.current?.querySelectorAll<HTMLElement>("[data-key]") ?? [])].find(x => x.dataset.key === follow);
    el?.scrollIntoView({ block: "nearest" });
  }, [follow]);

  const rowPage = (r: ScriptRow): Open => {
    if (r.kind === "picture") return { kind: "picture", t: r.t, editId: r.id };
    const b = lineById(ep, r.id);
    // A placeholder opens on Voice, to invite what is said; any other line on its Overview.
    return { kind: "line", t: r.t, editId: r.id, char: b?.char, ...(b?.placeholder ? { step: "voice" } : {}) };
  };
  const open = (r: ScriptRow) => { pb.stop(); setT(r.t); pages.openPage(rowPage(r)); }; // back from its page, the preview shows that row
  const [full, setFull] = useState(false); // playing full screen

  // Tapping the preview plays from where it is (from the start when it is at the end), or pauses.
  const togglePlay = () => {
    if (pb.playing) return pb.stop();
    if (playable) void pb.play(shownT >= total - 0.1 ? 0 : shownT);
  };

  // Add line and Add picture always add at the end of the episode. "+ Add here" adds at the end of its picture's card.
  const at = ep.lines.length ? total : 0;
  const noPictures = ep.pictures.length === 0;
  const [ins, setIns] = useState<number | null>(null); // where the "Insert here" sheet is open (the end of a card)
  // Audio on a picture whose only line is its untouched 1 s silence fills that line instead of adding another.
  const addAudio = (t: number, back: boolean) => {
    const ph = placeholderToFill(ep, t, back);
    pages.openPage(ph ? { kind: "line", t: ph.start, editId: ph.id, step: "voice" } : { kind: "line", t });
  };
  const insertAt = (what: "image" | "audio") => {
    if (ins === null) return;
    const t = ins;
    setIns(null);
    if (what === "audio") return addAudio(t, true);
    pages.openPage({ kind: "picture", t });
  };

  // Rows grouped by picture: a picture row starts a card and the lines after it belong to it.
  const groups = useMemo(() => {
    const out: { head?: Extract<ScriptRow, { kind: "picture" }>; rows: ScriptRow[] }[] = [];
    for (const r of rows) {
      if (r.kind === "picture") out.push({ head: r, rows: [] });
      else { if (!out.length) out.push({ rows: [] }); out[out.length - 1]!.rows.push(r); }
    }
    return out;
  }, [rows]);
  const name = (id: string) => findAsset(lib, id)?.name ?? "?";

  const lineRow = (r: Extract<ScriptRow, { kind: "line" }>) => {
    const b = lineById(ep, r.id);
    if (!b) return null;
    const now = playing?.key === r.key;
    const prog = now && pb.t !== null ? Math.max(0, Math.min(1, (pb.t - r.t) / Math.max(r.end - r.t, 0.001))) : 0;
    const cls = `sv-line${now ? " now" : ""}${b.status === "error" ? " error" : ""}`;
    const bar = now && <span className="sv-prog" aria-hidden="true" style={{ width: `${prog * 100}%` }} />;
    if (b.how === "silent") {
      const len = `${Math.round(b.duration * 10) / 10} s`;
      return (
        <button key={r.key} data-key={r.key} className={`${cls} silent`} aria-label="Open the silence" onClick={() => open(r)}>
          {bar}
          <span className="sv-icon"><Icon name="silence" size={22} /></span>
          <span className="sv-main">
            <span className="sv-who"><b>Silence · {len}</b></span>
            {b.placeholder && <span className="sv-fill">tap to add what's said</span>}
            <Notes b={r.badges} />
          </span>
        </button>
      );
    }
    const text = b.text?.trim() || (b.how === "recorded" ? "Recorded line" : "");
    return (
      <button key={r.key} data-key={r.key} className={cls} aria-label={`Open ${name(b.char ?? "")} line`} onClick={() => open(r)}>
        {bar}
        <Avatar assetId={b.char ?? ""} size={36} />
        <span className="sv-main">
          <span className="sv-who"><b>{name(b.char ?? "")}</b><span className="sv-dur">{fmtT(r.t)} · {secs(b.duration)}{r.voiceOver ? " · voice-over" : ""}</span></span>
          <span className="sv-text">{b.status === "syncing" ? "Making the voice…" : b.status === "error" ? `Didn't work. ${b.error ?? ""}` : text}</span>
          {b.status === "syncing" && <span className="sv-sync" />}
          <Notes b={r.badges} />
        </span>
      </button>
    );
  };

  return (
    <>
      <Page className="tl-editor sv">
        <EditorBar onBack={onBack} playable={playable} onExport={pages.exportIt} />
        <EpisodeHead ep={ep} t={shownT} playing={pb.playing} playable={playable} onToggle={togglePlay} tiles="script" view={view} onView={onView}
          onAddPicture={() => pages.openPage({ kind: "picture", t: noPictures ? 0 : at })} onFull={() => { pb.stop(); setFull(true); }}>
          <Tile icon="mic" label="Add line" sub={noPictures ? "Add a picture first" : "at the end"} disabled={noPictures} onClick={() => addAudio(at, true)} />
          <Tile icon="image" label="Add picture" sub="at the end" onClick={() => pages.openPage({ kind: "picture", t: noPictures ? 0 : at })} />
        </EpisodeHead>
        <div className="sv-list" ref={list}>
          {rows.length === 0 && <p className="hint sv-empty">Nothing here yet. Make the first picture, then give it a line.</p>}
          {groups.map((g, gi) => {
            const h = g.head;
            const m = h && markById(ep, "picture", h.id);
            const last = g.rows[g.rows.length - 1] ?? h;
            const sub = h && [fmtT(h.t), ...h.faces.map(name), h.motion && "animated"].filter(Boolean).join(" · ");
            return (
              <section key={h?.key ?? `g${gi}`} className="sv-card" aria-label={h ? `Picture ${h.n}` : "Lines"}>
                {h && (
                  <button data-key={h.key} className="sv-pic" aria-label={`Open picture ${h.n}`} onClick={() => open(h)}>
                    <span className="sv-thumb">{m && <img src={imageSrc(m.picture.src)} alt="" />}</span>
                    <span className="sv-main">
                      <b>Picture {h.n}</b>
                      <span className="sv-sub">{sub}</span>
                      {h.into !== undefined && <span className="sv-into">Starts {Math.round(h.into * 10) / 10} s into the {aboveNoun(rows, h)} above</span>}
                      {(jobs.has(picJob(h.id)) || jobs.has(motionJob(h.id))) && <span className="sv-badges">
                        {jobs.has(picJob(h.id)) && <span className="sv-badge sv-working"><span className="spinner" /> Drawing…</span>}
                        {jobs.has(motionJob(h.id)) && <span className="sv-badge sv-working"><span className="spinner" /> Animating…</span>}
                      </span>}
                    </span>
                    <span className="sv-chev"><Icon name="chevron" size={18} /></span>
                  </button>
                )}
                {g.rows.map(r => r.kind === "line" && lineRow(r))}
                {last && <button className="sv-add" onClick={() => setIns(last.after)}><Icon name="plus" size={16} /> Add here</button>}
              </section>
            );
          })}
          {drawingNew > 0 && <div className="sv-pending"><span className="spinner" /> Drawing {drawingNew > 1 ? `${drawingNew} new pictures` : "a new picture"}… It goes in when it's ready.</div>}
        </div>
        {ins !== null && (
          <div className="sheet-wrap" onClick={() => setIns(null)}>
            <div className="sheet" role="dialog" aria-label="Insert here" onClick={e => e.stopPropagation()}>
              <div className="sv-sheethead"><span className="sheet-title">Insert here</span>
                <button className="icon-btn subtle" onClick={() => setIns(null)} aria-label="Close"><Icon name="close" size={20} /></button></div>
              <div className="sheet-actions">
                <button className="btn big" onClick={() => insertAt("image")}><Icon name="image" /> Image</button>
                <button className="btn big" onClick={() => insertAt("audio")}><Icon name="voice" /> Audio</button>
              </div>
            </div>
          </div>
        )}
      </Page>
      {pages.node}
      {full && <FullPlayer ep={ep} from={shownT} onClose={time => { setFull(false); setT(time); }} />}
    </>
  );
}
